/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new MediaStartedPlayingEvent(element));
 * document.addEventListener(ThemeEvents.mediaStartedPlaying, (e) => { console.log(e.detail.resource) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a variant is selected */
  static variantSelected = 'variant:selected';
  /** @static @constant {string} Event triggered when a variant is changed */
  static variantUpdate = 'variant:update';
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlaying = 'media:started-playing';
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlayed = 'media:started-played';
  /** @static @constant {string} Event triggered when quantity-selector value is changed */
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when the cart is updated */
  static cartUpdate = 'cart:update';
  /** @static @constant {string} Event triggered when cart drawer update is complete */
  static cartUpdated = 'cart:updated';
  /** @static @constant {string} Event triggered when a cart update fails */
  static cartError = 'cart:error';
  /** @static @constant {string} Event triggered when changing collection filters */
  static FilterUpdate = 'filter:update';
  /** @static @constant {string} Event triggered when a megamenu open */
  static megaMenuOpen = 'megaMenu:open';
  /** @static @constant {string} Event triggered when a megamenu close */
  static megaMenuClose = 'megaMenu:close';
  /** @static @constant {string} Event triggered when free shipping message needs update */
  static freeShippingMessageUpdate = 'free-shipping-message:update';
  /** @static @constant {string} Event triggered when progress bar needs update */
  static progressBarUpdate = 'progress-bar:update';
  /** @static @constant {string} Reinitialize one progress-bar — dispatch on that element only (`bubbles: false`) */
  static progressBarReinit = 'progress-bar:reinit';
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

export class MediaStartedPlayedEvent extends Event {
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlayed, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

  /**
 * Event fired when a variant is selected
 * @extends {Event}
 */
export class VariantSelectedEvent extends Event {
  /**
   * Creates a new VariantSelectedEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdateEvent extends Event {
  /**
   * Creates a new VariantUpdateEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {boolean} resource.available - Whether the variant is available
   * @param {boolean} resource.inventory_management - Whether the variant has inventory management
   * @param {Object} [resource.featured_media] - The featured media of the variant
   * @param {string} [resource.featured_media.id] - The id of the featured media
   * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
   * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the variant
   * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
   * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
   * @param {string} data.newProduct.id - The id of the new product
   * @param {string} data.newProduct.url - The url of the new product
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
        isStickyAddToCart: data.isStickyAddToCart,
      },
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for cart additions
 * @extends {Event}
 */
export class CartAddEvent extends Event {
  /**
   * Creates a new CartAddEvent
   * @param {Object} [resource] - The new cart object
   * @param {string} [sourceId] - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - Total quantity in the cart after the operation; omit it unless that total is known
   * @param {string} [data.variantId] - The id of the product variant that was added
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }

  static eventName = ThemeEvents.cartUpdate;
}

/**
 * Event class for cart errors
 * @extends {Event}
 */
export class CartErrorEvent extends Event {
  /**
   * Creates a new CartErrorEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {string} message - A message from the server response
   */
  constructor(sourceId, message) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        message,
      },
    };
  }
}

/** Event class for facet filtering updates */
export class FilterUpdateEvent extends Event {
  /** @param {URLSearchParams} queryParams */
  constructor(queryParams) {
    super(ThemeEvents.FilterUpdate, { bubbles: true });
    this.detail = {
      queryParams,
    };
  }

  shouldShowClearAll() {
    return [...this.detail.queryParams.entries()].filter(([key]) => key.startsWith('filter.')).length > 0;
  }
}

/**
 * Event class for cart updates
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
  /**
   * Creates a new CartUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - Total quantity in the cart after the operation; omit it unless that total is known
   * @param {string} [data.variantId] - The id of the product variant that was updated
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }
}

/**
 * Event class for discount code updates.
 *
 * Applying a discount moves the same cart totals as any other update, so it carries
 * `CartUpdateEvent`'s name and payload; listeners that need to tell the two apart
 * use `instanceof`.
 * @extends {CartUpdateEvent}
 */
export class DiscountUpdateEvent extends CartUpdateEvent {}

/**
 * Event class for mega menu hover being hovered over
 * @extends {CustomEvent}
 * @param {HTMLElement} megaDropdown - The mega dropdown element
 */
export class MegaMenuOpenEvent extends CustomEvent {
  constructor(megaDropdown) {
    super(ThemeEvents.megaMenuOpen, {
      bubbles: true,
      detail: { megaDropdown }
    });
  }
}

/**
 * Event class for mega menu close
 * @extends {CustomEvent}
 * @param {HTMLElement} megaDropdown - The mega dropdown element
 */
export class MegaMenuCloseEvent extends CustomEvent {
  constructor(megaDropdown) {
    super(ThemeEvents.megaMenuClose, {
      bubbles: true,
      detail: { megaDropdown }
    });
  }
}

/**
 * Event class for free shipping message updates
 * @extends {Event}
 */
export class FreeShippingMessageUpdateEvent extends Event {
  /**
   * Creates a new FreeShippingMessageUpdateEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {string|number} data.cartTotal - The current cart total
   */
  constructor(sourceId, data) {
    super(ThemeEvents.freeShippingMessageUpdate, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        ...data,
      },
    };
  }
}

/**
 * Event class for progress bar updates
 * @extends {Event}
 */
export class ProgressBarUpdateEvent extends Event {
  /**
   * Creates a new ProgressBarUpdateEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {HTMLElement} data.progressBarElement - The progress bar element that was updated
   */
  constructor(sourceId, data) {
    super(ThemeEvents.progressBarUpdate, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        ...data,
      },
    };
  }
}

// Event classes - defined before components that use them
export class MenuDropdownOpenEvent extends CustomEvent {
  constructor() {
    super(MenuDropdownOpenEvent.eventName, {
      bubbles: true,
      composed: true
    });
  }

  static eventName = 'menu-dropdown:open';
}

export class MenuDropdownCloseEvent extends CustomEvent {
  constructor() {
    super(MenuDropdownCloseEvent.eventName, {
      bubbles: true,
      composed: true
    });
  }

  static eventName = 'menu-dropdown:close';
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName, {
      bubbles: true,
      composed: true
    });
  }

  static eventName = 'dialog:open';
}

export class DialogBeforeCloseEvent extends CustomEvent {
  /**
   * @param {Event} [originalEvent] - Original event that triggered the close
   */
  constructor(originalEvent) {
    super(DialogBeforeCloseEvent.eventName, {
      bubbles: true,
      composed: true,
      detail: { originalEvent }
    });
  }

  static eventName = 'dialog:before-close';
}

export class DialogCloseEvent extends CustomEvent {
  /**
   * @param {Event} [originalEvent] - Original event that triggered the close
   */
  constructor(originalEvent) {
    super(DialogCloseEvent.eventName, {
      bubbles: true,
      composed: true,
      detail: { originalEvent }
    });
  }

  static eventName = 'dialog:close';
}

export class DropdownDialogOpenEvent extends CustomEvent {
  constructor() {
    super(DropdownDialogOpenEvent.eventName, {
      bubbles: true,
      composed: true
    });
  }

  static eventName = 'dropdown-dialog:open';
}

export class DropdownDialogCloseEvent extends CustomEvent {
  constructor() {
    super(DropdownDialogCloseEvent.eventName, {
      bubbles: true,
      composed: true
    });
  }

  static eventName = 'dropdown-dialog:close';
}